<template lang="pug">
.record-download-page.flex.align-start.column.gap-20
  .desc 選擇年份和月份，將打包選定範圍的個案記錄，動作完成後即可下載檔案
  .flex.align-center.gap-12
    el-date-picker(format="yyyy-MM", v-model="month", type="month", placeholder="選擇年份和月份")
    place-selector(show-all, v-model="branch")
    el-button(type="primary", @click="sendArchive") 打包檔案
  .table.flex
    el-table(:data="showArchives", border, style="width: 100%")
      el-table-column(label="打包月份", width="120")
        template(slot-scope="scope")
          span {{ $timestampToMonth(scope.row.start_from) }}
      el-table-column(label="打包館別", width="150")
        template(slot-scope="scope")
          span {{ branchName(scope.row.branch_id) }}
      el-table-column(label="打包時間", width="150")
        template(slot-scope="scope")
          span {{ $timestampToDateTimeMinute(scope.row.created_at) }}
      el-table-column(label="狀態", width="150")
        template(slot-scope="scope")
          template(v-if="scope.row.status === 'done'")
            span.success-color 檔案已完成
          template(v-else-if="scope.row.status === 'failed'")
            span.danger-color 打包失敗
          template(v-else)
            span.warn-color 檔案正在打包中...
      el-table-column(label="檔案下載", width="150")
        template(slot-scope="scope")
          template(v-if="scope.row.status === 'done'")
            el-button(type="primary", @click="findArchive(scope.row)") 下載
          template(v-else-if="scope.row.status === 'failed'")
            el-button(type="warning", @click="retryArchive(scope.row)") 重新打包
</template>

<script>
import {
  createArchives,
  getArchives,
} from '@/api/archive';
import { getBranches } from '@/api/place';

export default {
  data() {
    return {
      month: undefined,
      branch: undefined,

      page: 1,
      limit: 10,
      archives: [],
      branches: [],

      syncGen: 0,
      syncTimer: undefined,
    };
  },
  computed: {
    showArchives() {
      const startIdx = (this.page - 1) * this.limit;
      const endIdx = startIdx + this.limit;
      return this.archives.slice(startIdx, endIdx);
    },
    branchMap() {
      const map = {};
      this.branches.forEach((b) => { map[b.id] = b.name; });
      return map;
    },
  },
  methods: {
    branchName(branchID) {
      if (!branchID) return '';
      return this.branchMap[branchID] || '';
    },
    sendArchive() {
      if (!this.month) {
        this.$showError('請選擇年份和月份');
        return;
      }
      const start = this.month;
      start.setMonth(start.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 1, 1);
      this.$execWithLoading(async () => {
        await createArchives(this.$dateToTimestamp(start), this.$dateToTimestamp(end), this.branch);
        this.archives = await getArchives();
        this.$showSuccess('已送出打包請求');
        this.loopSync();
      }, (e) => {
        this.$showAxiosException('打包下載檔案失敗', e);
      });
    },
    downloadFile() {
      const start = this.month;
      start.setMonth(start.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 1, 1);
      this.$execWithLoading(async () => {
        await createArchives(this.$dateToTimestamp(start), this.$dateToTimestamp(end));
        this.archives = await getArchives();
        this.loopSync();
      }, (e) => {
        this.$showAxiosException('打包下載檔案失敗', e);
      });
    },
    // 打包失敗時以同樣的月份與館別重新送出打包
    retryArchive(record) {
      let endAt = record.end_at;
      if (!endAt) {
        // 舊版後端未回傳 end_at，依打包月份推算下個月 1 日
        const end = new Date(record.start_from * 1000);
        end.setMonth(end.getMonth() + 1, 1);
        endAt = this.$dateToTimestamp(end);
      }
      this.$execWithLoading(async () => {
        await createArchives(record.start_from, endAt, record.branch_id || undefined);
        this.archives = await getArchives();
        this.$showSuccess('已重新送出打包請求');
        this.loopSync();
      }, (e) => {
        this.$showAxiosException('重新打包失敗', e);
      });
    },
    async findArchive(record) {
      window.open(record.url);
    },
    async loadList() {
      return this.$execWithLoading(async () => {
        this.archives = await getArchives();
      }, (e) => {
        this.$showAxiosException('載入打包檔案失敗', e);
      });
    },
    async loadBranches() {
      try {
        this.branches = await getBranches();
      } catch (e) {
        this.branches = [];
      }
    },
    // 每 5 秒輪詢一次，直到所有打包都完成或失敗；送出新打包時會重新啟動
    loopSync() {
      this.stopSync();
      const gen = this.syncGen;
      const loop = async () => {
        if (gen !== this.syncGen) return;
        const needSync = this.archives.some((a) => a.status !== 'done' && a.status !== 'failed');
        if (!needSync) return;
        try {
          const archives = await getArchives();
          if (gen !== this.syncGen) return;
          this.archives = archives;
        } catch (e) {
          console.log('sync archives fail', e);
        }
        if (gen !== this.syncGen) return;
        this.syncTimer = setTimeout(loop, 5000);
      };
      this.syncTimer = setTimeout(loop, 5000);
    },
    stopSync() {
      this.syncGen += 1;
      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
        this.syncTimer = undefined;
      }
    },
  },
  mounted() {
    this.loadBranches();
    this.loadList().then(() => {
      this.loopSync();
    });
  },
  beforeDestroy() {
    this.stopSync();
  },
};
</script>

<style lang="scss">
@import '@/assets/style/variables.scss';
.record-download-page {
  & > .table {
    overflow: hidden;
  }

  // hack
  .el-table {
    border: 1px solid #EBEEF5;
    border-bottom: none;
    border-left: none;
    display: flex;
    flex-direction: column;

    // handle scroll
    .el-table__header-wrapper {
      flex: 0 0 auto;
    }
    .el-table__body-wrapper {
      flex: 1;
      overflow: overlay;
    }
  }
}
</style>
